import { parseJsonObject } from './career-adjustment'

/**
 * Ideal- und Übergangsbereich einer Stelle je Dimension, für das Teamprofil in
 * Discover. Quelle ist career_analyse.scores, einmal von synthesizeAnalyze
 * berechnet (Auswahlschärfe eingerechnet, auf 0 bis 100 begrenzt). Das Matching
 * liest dieselben Werte. Gefiltert wird wie im Backend-Anforderungsprofil
 * (helperControl/text-generation/REQUIREMENTPROFILE), damit das Teamprofil keine
 * Anforderung zeigt, die das Anforderungsprofil ausblendet. Kultur bleibt außen
 * vor, sie ist im Teamprofil keine Skala.
 *
 * Bewusst ohne React und ohne helpers/index, damit Jest die Datei ohne Mocks lädt.
 */

const EXCLUDED_DIMENSIONS = [
  'FLANKER_score',
  'TOWER_OF_LONDON_score',
  'BIG_FIVE_elevation'
]

// Nur diese Kategorien zeigt das Teamprofil als Skala, 4 ist Kultur
const SCALE_CATEGORIES = [1, 2, 3]

const isRiasecKey = (key) => key.startsWith('RIASEC_IMAGES')
const isCultureKey = (key) => key.startsWith('HEROS_JOURNEY')

// parseJsonObject auf ein Objekt ergäbe {}, deshalb Objekte durchreichen
const toObject = (value) =>
  value && typeof value === 'object' ? value : parseJsonObject(value)

const clampPercent = (value) => Math.min(100, Math.max(0, value))

/**
 * v1-Stellen ({ min, max, optimal }), nicht synthetisierte Stellen und die
 * RIASEC-Ränge ({ optimal }) haben keine Bereiche und ergeben null.
 * Die Namen entsprechen den Props von BeamGraph.
 */
export const toBands = (score) => {
  if (!score || typeof score !== 'object') return null

  const values = [
    score.min_yellow,
    score.max_yellow,
    score.min_green,
    score.max_green
  ]
  if (!values.every(Number.isFinite)) return null

  const [minYellow, maxYellow, minGreen, maxGreen] = values.map(clampPercent)
  if (minYellow > maxYellow || minGreen > maxGreen) return null

  return { minYellow, maxYellow, minGreen, maxGreen }
}

/**
 * @returns {{ [dimensionKey: string]: { bands: object, mandatory: boolean } }}
 */
export const getRequirementBands = ({ careerAnalyse, reportSettings } = {}) => {
  const analyse = toObject(careerAnalyse)
  const scores = toObject(analyse.scores)
  const settings = toObject(reportSettings)

  const hidden = Array.isArray(settings.hidden_dimensions)
    ? settings.hidden_dimensions
    : []
  const interestsVisible = settings.interests_visible !== false

  // Wie im Backend ist dimensions die Allowlist, RIASEC-Keys aus scores kommen immer dazu
  const baseKeys = Array.isArray(analyse.dimensions)
    ? analyse.dimensions
    : Object.keys(scores)
  const riasecKeys = Object.keys(scores).filter(isRiasecKey)

  return [...new Set([...baseKeys, ...riasecKeys])]
    .filter((key) => typeof key === 'string')
    .filter((key) => !hidden.includes(key))
    .filter((key) => !EXCLUDED_DIMENSIONS.includes(key))
    .filter((key) => !isCultureKey(key))
    .filter((key) => interestsVisible || !isRiasecKey(key))
    .reduce((result, key) => {
      const bands = toBands(scores[key])
      if (bands) {
        result[key] = {
          bands,
          mandatory: scores[key].mandatoryCriterion === true
        }
      }
      return result
    }, {})
}

// Die einzelnen Interessen stehen in scores nur als Rang, nie mit Bereichen.
// Die Interessenvielfalt (elevation) hat dagegen Bereiche wie jede Skala.
const isRiasecLetterKey = (key) =>
  isRiasecKey(key) && key !== 'RIASEC_IMAGES_elevation'

/**
 * Zählt nur Skalen, die das Teamprofil als DimensionCard zeigt.
 * Interessen ohne Bereich zählen extra, weil sie nie Bereiche haben und
 * direkt an der Interessen-Überschrift erklärt werden, nicht als Lücke.
 */
export const getBandCoverage = (bandsByKey, dimensionsWithTextModules) => {
  const scales = (
    Array.isArray(dimensionsWithTextModules) ? dimensionsWithTextModules : []
  )
    .map((item) => item?.dimension)
    .filter((dimension) => SCALE_CATEGORIES.includes(dimension?.category))

  const hasBands = (dimension) => !!bandsByKey?.[dimension.key]
  const unbanded = scales.filter((dimension) => !hasBands(dimension))
  const unbandedInterests = unbanded.filter(({ key }) =>
    isRiasecLetterKey(String(key))
  ).length

  return {
    withBands: scales.length - unbanded.length,
    withoutBands: unbanded.length - unbandedInterests,
    unbandedInterests,
    mandatory: scales.filter(
      (dimension) => bandsByKey?.[dimension.key]?.mandatory
    ).length
  }
}
